import React, { useState } from "react";
import {
  Box,
  Button,
  Collapse,
  IconButton,
  InputAdornment,
  TextField,
  Typography,
  useMediaQuery,
} from "@mui/material";
import { Visibility, VisibilityOff } from "@mui/icons-material";
import AddCircleOutlineIcon from "@mui/icons-material/AddCircleOutline";
import RemoveCircleOutlineIcon from "@mui/icons-material/RemoveCircleOutline";
import phoneCodes from "../../../utils/static/phoneCodes";
import { SearchableDropdown } from "../../utils/Form/Dropdown";

const countryNames = new Intl.DisplayNames(["en"], { type: "region" });
const phoneCodeOptions = phoneCodes.map(({ code, country }) => ({
  value: code,
  label: countryNames.of(country === "UK" ? "GB" : country) || country,
  reference: code,
  searchText: `${country} ${country === "UK" ? "GB Britain" : country === "US" ? "USA America" : ""}`,
}));

const roundedTextFieldSx = {
  "& .MuiOutlinedInput-root": { borderRadius: 2, minHeight: 48 },
  "& .MuiOutlinedInput-input": { fontSize: 14, fontWeight: 500 },
};

export const RegistrationSection = ({ id, title, description, children }) => (
  <Box component='section' aria-labelledby={id}>
    <Typography id={id} component='h2' variant='subtitle1' fontWeight={700}>
      {title}
    </Typography>
    {description && (
      <Typography variant='body2' color='text.secondary' sx={{ mt: 0.5 }}>
        {description}
      </Typography>
    )}
    <Box
      sx={{
        display: "grid",
        gridTemplateColumns: "minmax(0, 1fr)",
        gap: 2,
      }}
    >
      {children}
    </Box>
  </Box>
);

export const fieldProps = (formik, name) => ({
  id: `register-${name}`,
  name,
  value: formik.values[name],
  onChange: formik.handleChange,
  onBlur: formik.handleBlur,
  error: Boolean(formik.touched[name] && formik.errors[name]),
  helperText: formik.touched[name] && formik.errors[name],
  fullWidth: true,
});

const PasswordField = ({ formik, name, label, disabled, onFocus, onBlur }) => {
  const [visible, setVisible] = useState(false);
  return (
    <TextField
      {...fieldProps(formik, name)}
      label={label}
      required
      disabled={disabled}
      type={visible ? "text" : "password"}
      autoComplete='new-password'
      onFocus={onFocus}
      onBlur={(event) => {
        formik.handleBlur(event);
        onBlur?.(event);
      }}
      InputProps={{
        endAdornment: (
          <InputAdornment position='end'>
            <IconButton
              aria-label={`${visible ? "Hide" : "Show"} ${name === "confirmPassword" ? "confirmation password" : "password"}`}
              onClick={() => setVisible(!visible)}
              onMouseDown={(event) => event.preventDefault()}
              disabled={disabled}
              edge='end'
            >
              {visible ? <VisibilityOff /> : <Visibility />}
            </IconButton>
          </InputAdornment>
        ),
      }}
    />
  );
};

export const ContactFields = ({
  formik,
  disabled,
  nameLocked,
  emailLocked,
}) => (
  <RegistrationSection id='register-account-title'>
    <TextField
      {...fieldProps(formik, "name")}
      fullWidth
      label='Full name'
      required
      autoComplete='name'
      disabled={disabled || nameLocked}
      sx={roundedTextFieldSx}
    />

    <TextField
      {...fieldProps(formik, "email")}
      fullWidth
      label='Email address'
      required
      autoComplete='email'
      disabled={disabled || emailLocked}
      inputProps={{
        inputMode: "email",
        autoCapitalize: "none",
        spellCheck: false,
      }}
      sx={roundedTextFieldSx}
    />

    <Box
      sx={{
        gridColumn: "1 / -1",
        display: "grid",
        gridTemplateColumns: {
          xs: "minmax(0, 1fr)",
        },
        "@media (min-width: 360px)": {
          gridTemplateColumns: "115px minmax(0, 1fr)",
        },
        gap: 1.5,
        alignItems: "start",
        minWidth: 0,
      }}
    >
      <SearchableDropdown
        id='register-phoneCode'
        name='phoneCode'
        value={formik.values.phoneCode}
        options={phoneCodeOptions}
        onChange={(value) => formik.setFieldValue("phoneCode", value)}
        onBlur={() => formik.setFieldTouched("phoneCode", true)}
        label='Code'
        ariaLabel='Country code'
        placeholder='Search'
        noOptionsText='No matching countries'
        required
        disabled={disabled}
        disableClearable
        error={Boolean(formik.touched.phoneCode && formik.errors.phoneCode)}
        helperText={formik.touched.phoneCode && formik.errors.phoneCode}
        getOptionLabel={(option) => option.reference}
        maxListHeight={300}
        popperSx={{ minWidth: 280, maxWidth: "calc(100vw - 32px)" }}
        sx={{
          minWidth: 0,
          "& .MuiOutlinedInput-root": { minHeight: 56 },
          "& .MuiAutocomplete-input": { fontSize: 14 },
        }}
      />

      <TextField
        {...fieldProps(formik, "phoneNumber")}
        fullWidth
        label='Phone number'
        required
        autoComplete='tel-national'
        inputProps={{
          inputMode: "tel",
        }}
        disabled={disabled}
        sx={{ ...roundedTextFieldSx, minWidth: 0 }}
      />
    </Box>
  </RegistrationSection>
);

export const PasswordFields = ({ formik, disabled }) => {
  const [passwordFocused, setPasswordFocused] = useState(false);
  const reduceMotion = useMediaQuery("(prefers-reduced-motion: reduce)");
  return (
    <RegistrationSection id='register-password-title'>
      <Box>
        <PasswordField
          formik={formik}
          name='password'
          label='Password'
          disabled={disabled}
          onFocus={() => setPasswordFocused(true)}
          onBlur={() => setPasswordFocused(false)}
          sx={roundedTextFieldSx}
        />
        <Collapse
          in={passwordFocused}
          timeout={reduceMotion ? 0 : { enter: 220, exit: 180 }}
        >
          <Typography
            variant='body2'
            color='text.secondary'
            sx={{
              pt: 1.5,
              opacity: passwordFocused ? 1 : 0,
              transition: reduceMotion ? "none" : "opacity 180ms ease",
            }}
          >
            Use at least 8 characters with a letter, a number and a symbol.
            Allowed symbols: @ $ ! % * # ? &. No spaces or other symbols.
          </Typography>
        </Collapse>
      </Box>
      <PasswordField
        formik={formik}
        name='confirmPassword'
        label='Confirm password'
        disabled={disabled}
        sx={roundedTextFieldSx}
      />
    </RegistrationSection>
  );
};

export const OrganizationFields = ({ formik, disabled }) => {
  const [showGST, setShowGST] = useState(Boolean(formik.values.orgGSTNo));
  const gstButtonSx = {
    justifySelf: "start",
    alignSelf: "flex-start",
    px: 1,
    minWidth: 0,
    minHeight: 40,
    textTransform: "none",
    fontSize: "13px",
    fontWeight: 500,
    "& .MuiButton-startIcon": { ml: 0 },
  };
  return (
    <Box
      component='section'
      aria-labelledby='register-organization-title'
      sx={{ display: "grid", gridTemplateColumns: "minmax(0, 1fr)", gap: 2 }}
    >
      <Box sx={{ display: "grid", gap: 0.5 }}>
        <Typography
          id='register-organization-title'
          component='h2'
          sx={{
            fontSize: 16,
            fontWeight: 700,
            color: "text.primary",
          }}
        >
          Name your organization
        </Typography>

        <Typography variant='body2' color='text.secondary' sx={{ mt: -0.5 }}>
          This name will identify your organization's tax workspace.
        </Typography>
      </Box>

      <Box sx={{ display: "grid", gap: 1 }}>
        <TextField
          {...fieldProps(formik, "orgName")}
          placeholder='Enter your organization name'
          required
          size='small'
          autoComplete='organization'
          disabled={disabled}
          sx={roundedTextFieldSx}
        />
      </Box>

      {showGST ? (
        <Box
          sx={{
            gridColumn: "1 / -1",
            display: "flex",
            flexDirection: "column",
            gap: 1,
          }}
        >
          <TextField
            {...fieldProps(formik, "orgGSTNo")}
            label='GST registration number'
            disabled={disabled}
            fullWidth
            sx={roundedTextFieldSx}
          />

          <Button
            type='button'
            variant='text'
            size='small'
            color='error'
            startIcon={<RemoveCircleOutlineIcon />}
            disabled={disabled}
            onClick={() => {
              formik.setFieldValue("orgGSTNo", "");
              formik.setFieldTouched("orgGSTNo", false, false);
              setShowGST(false);
            }}
            sx={gstButtonSx}
          >
            Remove GST number
          </Button>
        </Box>
      ) : (
        <Button
          type='button'
          variant='text'
          size='small'
          startIcon={<AddCircleOutlineIcon />}
          disabled={disabled}
          onClick={() => setShowGST(true)}
          sx={gstButtonSx}
        >
          Add GST registration number (optional)
        </Button>
      )}
    </Box>
  );
};
